// feature/lifecycle-redesign.
// Angie's "Para archivar" queue. Shows every case in pendiente_archivar
// (i.e., the lawyer answered, awaiting Reparto's terminal archive).
// "Ver / archivar" opens a centered modal with the contestación
// metadata + a free-form notes field; "Archivado" terminally closes.

import React, { useEffect, useState } from "react";
import CenteredModal from "../../components/CenteredModal";
import Spinner from "../../components/Spinner";
import { TipoPill } from "../../components/Pills";
import { paraArchivar, cases as casesApi } from "../../api/endpoints";
import { useToast } from "../../components/Toast";
import { CONTESTADA_LABELS } from "../../utils/permissions";

const fmt = (d) => d ? new Date(d).toLocaleString("es-CO") : "—";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("es-CO") : "—";

export default function ParaArchivar() {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [activeCase, setActiveCase] = useState(null);

  function load() {
    setData(null);
    paraArchivar.list()
      .then((r) => setData(r.data?.cases || []))
      .catch(() => { toast.push("No se pudo cargar la cola de archivar.", "error"); setData([]); });
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="col" style={{ gap: 16 }}>
      <header>
        <h1 style={{ margin: 0 }}>Para archivar</h1>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          Casos que los abogados ya contestaron. Revise y archive para cerrarlos definitivamente.
        </div>
      </header>

      {data == null ? (
        <div className="card tight" style={{ padding: 32, textAlign: "center" }}>
          <Spinner size={20} />
        </div>
      ) : data.length === 0 ? (
        <div className="card tight" style={{ padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
          <div style={{ fontSize: 14 }}>No hay casos pendientes de archivar.</div>
          <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
            Cuando un abogado marque un caso como contestada aparecerá aquí.
          </div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <table>
            <thead>
              <tr>
                <th>Caso</th>
                <th>Tipo</th>
                <th>Accionante</th>
                <th>Abogado</th>
                <th>Categoría</th>
                <th>Contestado</th>
                <th>Hace</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map((c) => (
                <tr key={c.id}>
                  <td><strong>{c.case_number}</strong></td>
                  <td><TipoPill tipo={c.tipo} /></td>
                  <td>{c.accionante || "—"}</td>
                  <td>{c.abogado_name || c.abogado_email || "—"}</td>
                  <td>{CONTESTADA_LABELS[c.contestada_categoria] || c.contestada_categoria || "—"}</td>
                  <td>{fmtDate(c.contestada_at)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{howLong(c.pendiente_archivar_at)}</td>
                  <td>
                    <button className="secondary sm" onClick={() => setActiveCase(c)}>
                      Ver / archivar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeCase && (
        <ArchivarModal
          caseData={activeCase}
          onClose={() => setActiveCase(null)}
          onArchived={() => { setActiveCase(null); load(); toast.push("Caso archivado."); }}
        />
      )}
    </div>
  );
}

function howLong(d) {
  if (!d) return "—";
  const ms = Date.now() - new Date(d).getTime();
  const h = ms / 3600 / 1000;
  if (h < 1)  return `${Math.max(1, Math.round(ms / 60000))} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${Math.round(h / 24)} d`;
}

function ArchivarModal({ caseData, onClose, onArchived }) {
  const toast = useToast();
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);

  async function submit() {
    setBusy(true);
    try {
      await casesApi.archivar(caseData.id, { notes: notes.trim() || null });
      onArchived?.();
    } catch (err) {
      toast.push(err?.response?.data?.error || "Error al archivar", "error");
    } finally { setBusy(false); }
  }

  return (
    <CenteredModal
      open={!!caseData}
      onClose={busy ? undefined : onClose}
      title={caseData.case_number}
      subtitle={`${caseData.accionante || "—"} · ${caseData.tipo}`}
      headerTags={
        <>
          <TipoPill tipo={caseData.tipo} />
          <span className="badge mint" style={{ fontSize: 10 }}>Por archivar</span>
        </>
      }
      size="md"
      footer={<>
        <button className="secondary" onClick={onClose} disabled={busy}>Cancelar</button>
        <button onClick={submit} disabled={busy}>
          {busy && <Spinner color="currentColor" />} Archivado
        </button>
      </>}
    >
      <section style={{ marginBottom: 16 }}>
        <h3 style={{
          margin: "0 0 8px", fontSize: 12, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: "0.4px",
          color: "var(--text-muted)",
        }}>📋 Caso</h3>
        <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "120px 1fr" }}>
          <dt>Accionante</dt><dd>{caseData.accionante || "—"}</dd>
          <dt>Radicado</dt>
          <dd style={{ fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace", fontSize: 12 }}>
            {caseData.radicado || "—"}
          </dd>
          <dt>Juzgado</dt><dd>{caseData.juzgado || "—"}</dd>
          <dt>Abogado</dt><dd>{caseData.abogado_name || caseData.abogado_email || "—"}</dd>
        </dl>
      </section>

      <section style={{ marginBottom: 16 }}>
        <h3 style={{
          margin: "0 0 8px", fontSize: 12, fontWeight: 600,
          textTransform: "uppercase", letterSpacing: "0.4px",
          color: "var(--text-muted)",
        }}>📨 Contestación</h3>
        <dl className="kvs" style={{ fontSize: 13, gridTemplateColumns: "120px 1fr" }}>
          <dt>Categoría</dt>
          <dd>{CONTESTADA_LABELS[caseData.contestada_categoria] || caseData.contestada_categoria || "—"}</dd>
          <dt>Contestada</dt><dd>{fmt(caseData.contestada_at)}</dd>
          {caseData.contestada_notas && (
            <>
              <dt>Notas del abogado</dt>
              <dd style={{ whiteSpace: "pre-wrap" }}>{caseData.contestada_notas}</dd>
            </>
          )}
        </dl>
      </section>

      <label style={{ display: "block", textTransform: "none", letterSpacing: 0, fontSize: 13 }}>
        Notas de archivo (opcional, máx 500 caracteres)
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={3} maxLength={500} disabled={busy}
                  placeholder="Ej: Verificado correo enviado a juzgado el…"
                  style={{ marginTop: 4, width: "100%" }} />
      </label>
    </CenteredModal>
  );
}
